import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SaveIcon, ArrowLeftIcon, HelpCircleIcon, MapPinIcon, CheckIcon, XIcon, UploadIcon, AlertCircleIcon, RefreshCwIcon, HistoryIcon, BuildingIcon, ArrowRightIcon, PlusIcon, TrashIcon, CalendarIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
import { PaymentMethodsTab, MDRConfig } from '../../components/merchants/PaymentMethodsTab';
// Mock parent merchants data for dropdown
const parentMerchantsData = [{
  id: 'PM001',
  name: 'Global Retail Group'
}, {
  id: 'PM002',
  name: 'Fast Food Alliance'
}, {
  id: 'PM003',
  name: 'Tech Distribution Network'
}, {
  id: 'PM004',
  name: 'Hospitality Group International'
}, {
  id: 'PM005',
  name: 'Urban Transport Consortium'
}];
// Payment modes for limit management
const paymentModes = ['POS', 'SoftPos', 'Online', 'Mobile POS', 'QR Payments', 'RTP Payments'];
// Frequency options for limit management
const frequencyOptions = ['Daily', 'Monthly', 'Yearly', 'Per Transaction'];
const MerchantForm: React.FC = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [activeTab, setActiveTab] = useState('merchant-kyc');
  const [formStatus, setFormStatus] = useState<'draft' | 'submitted' | 'approved' | 'rejected'>('draft');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showStatusHistoryModal, setShowStatusHistoryModal] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [statusChangeReason, setStatusChangeReason] = useState('');
  // Mock status history for editing
  const statusHistory = isEditing ? [{
    previousStatus: 'pending',
    newStatus: 'active',
    changedBy: 'admin@example.com',
    changedAt: '2023-05-20T10:30:00Z',
    reason: 'All requirements met'
  }] : [];
  // Mock merchant data for editing
  const merchantData = isEditing ? {
    name: 'ABC Corporation',
    businessType: 'Retail',
    cnic: '12345-6789012-3',
    ntn: 'NTN-12345',
    registrationNumber: 'REG-98765',
    verificationStatus: 'verified',
    businessName: 'ABC Corporation',
    industryType: 'Retail',
    mcc: '5411',
    address: '123 Main St, Anytown',
    contactPerson: 'John Smith',
    contactEmail: 'john@abccorp.com',
    contactPhone: '+1 (555) 123-4567',
    parentMerchantId: '',
    settlementType: 'individual',
    bankAccount: '1234567890',
    bankName: 'First National Bank',
    branchCode: 'FNB-001',
    settlementCurrency: 'USD',
    settlementFrequency: 'daily',
    dailyLimit: 10000,
    monthlyLimit: 200000,
    rtpLimit: 5000,
    refundLimit: 2000,
    allowedPaymentMethods: ['pos', 'qr', 'online'],
    allowedCurrencies: ['USD', 'EUR'],
    documents: {
      cnic: {
        name: 'cnic.pdf',
        uploaded: true
      },
      proofOfAddress: {
        name: 'address.pdf',
        uploaded: true
      },
      businessRegistration: {
        name: 'registration.pdf',
        uploaded: true
      },
      bankLetter: {
        name: 'bank_letter.pdf',
        uploaded: true
      }
    },
    complianceStatus: 'passed',
    complianceNotes: 'All checks passed',
    location: {
      lat: 40.7128,
      lng: -74.006
    },
    status: 'active',
    statusHistory: statusHistory,
    lastActivityDate: '2023-10-25',
    limits: [{
      id: '1',
      paymentMode: 'POS',
      amount: 5000,
      frequency: 'Daily',
      effectiveDate: '2023-11-01',
      endDate: '2023-12-31'
    }, {
      id: '2',
      paymentMode: 'QR Payments',
      amount: 50000,
      frequency: 'Monthly',
      effectiveDate: '2023-11-01',
      endDate: null
    }]
  } : {
    name: '',
    businessType: '',
    cnic: '',
    ntn: '',
    registrationNumber: '',
    verificationStatus: 'pending',
    businessName: '',
    industryType: '',
    mcc: '',
    address: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    parentMerchantId: '',
    settlementType: 'individual',
    bankAccount: '',
    bankName: '',
    branchCode: '',
    settlementCurrency: 'USD',
    settlementFrequency: 'daily',
    dailyLimit: 5000,
    monthlyLimit: 100000,
    rtpLimit: 1000,
    refundLimit: 1000,
    allowedPaymentMethods: ['pos'],
    allowedCurrencies: ['USD'],
    documents: {
      cnic: {
        name: '',
        uploaded: false
      },
      proofOfAddress: {
        name: '',
        uploaded: false
      },
      businessRegistration: {
        name: '',
        uploaded: false
      },
      bankLetter: {
        name: '',
        uploaded: false
      }
    },
    complianceStatus: 'pending',
    complianceNotes: '',
    location: {
      lat: 0,
      lng: 0
    },
    status: 'pending',
    statusHistory: [],
    lastActivityDate: new Date().toISOString().split('T')[0],
    limits: []
  };
  const [merchant, setMerchant] = useState(merchantData);
  const [paymentConfigs, setPaymentConfigs] = useState<MDRConfig[]>([]);
  const [newLimit, setNewLimit] = useState({
    paymentMode: '',
    amount: 0,
    frequency: '',
    effectiveDate: '',
    endDate: ''
  });
  const [limitError, setLimitError] = useState('');
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setMerchant(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleNewLimitChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setNewLimit(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const validateLimitDates = () => {
    // Check if dates are valid
    if (!newLimit.effectiveDate) {
      setLimitError('Effective date is required');
      return false;
    }
    const effectiveDate = new Date(newLimit.effectiveDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (effectiveDate < today) {
      setLimitError('Effective date must be current or future date');
      return false;
    }
    if (newLimit.endDate) {
      const endDate = new Date(newLimit.endDate);
      if (endDate <= effectiveDate) {
        setLimitError('End date must be greater than effective date');
        return false;
      }
    }
    // Check for overlapping dates with same payment mode and frequency
    const overlapping = merchant.limits.some(limit => limit.paymentMode === newLimit.paymentMode && limit.frequency === newLimit.frequency && (newLimit.endDate && limit.endDate ? new Date(newLimit.effectiveDate) <= new Date(limit.endDate) && new Date(newLimit.endDate) >= new Date(limit.effectiveDate) : newLimit.endDate ? new Date(limit.effectiveDate) <= new Date(newLimit.endDate) : limit.endDate ? new Date(newLimit.effectiveDate) <= new Date(limit.endDate) : true));
    if (overlapping) {
      setLimitError('Date ranges overlap with an existing limit for the same payment mode and frequency');
      return false;
    }
    return true;
  };
  const addNewLimit = () => {
    if (!newLimit.paymentMode || !newLimit.frequency || !newLimit.amount || !newLimit.effectiveDate) {
      setLimitError('Please fill all required fields');
      return;
    }
    if (!validateLimitDates()) {
      return;
    }
    const limitToAdd = {
      id: Date.now().toString(),
      ...newLimit
    };
    setMerchant(prev => ({
      ...prev,
      limits: [...prev.limits, limitToAdd]
    }));
    // Reset form
    setNewLimit({
      paymentMode: '',
      amount: 0,
      frequency: '',
      effectiveDate: '',
      endDate: ''
    });
    setLimitError('');
  };
  const removeLimit = (id: string) => {
    setMerchant(prev => ({
      ...prev,
      limits: prev.limits.filter(limit => limit.id !== id)
    }));
  };
  const handleStatusChange = () => {
    setNewStatus(merchant.status);
    setShowStatusModal(true);
  };
  const confirmStatusChange = () => {
    if (!newStatus || newStatus === merchant.status) {
      setShowStatusModal(false);
      return;
    }
    // Update merchant status and add to history
    const statusHistoryItem = {
      previousStatus: merchant.status,
      newStatus: newStatus,
      changedBy: 'current.user@example.com',
      changedAt: new Date().toISOString(),
      reason: statusChangeReason
    };
    setMerchant(prev => ({
      ...prev,
      status: newStatus,
      statusHistory: [statusHistoryItem, ...prev.statusHistory]
    }));
    setShowStatusModal(false);
    setStatusChangeReason('');
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    const methodName = name.replace('payment-', '');
    setMerchant(prev => ({
      ...prev,
      allowedPaymentMethods: checked ? [...prev.allowedPaymentMethods, methodName] : prev.allowedPaymentMethods.filter(m => m !== methodName)
    }));
  };
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    const currency = name.replace('currency-', '');
    setMerchant(prev => ({
      ...prev,
      allowedCurrencies: checked ? [...prev.allowedCurrencies, currency] : prev.allowedCurrencies.filter(c => c !== currency)
    }));
  };
  const handleFileUpload = (documentType: keyof typeof merchant.documents) => {
    // Mock file upload
    setMerchant(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [documentType]: {
          name: `${documentType}_${Date.now()}.pdf`,
          uploaded: true
        }
      }
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Submit logic would go here
    setFormStatus('submitted');
    // Navigate back to merchant list
    setTimeout(() => {
      navigate('/merchants');
    }, 1500);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-5352" />;
      case 'inactive':
        return <StatusBadge status="danger" text="Inactive" data-id="element-5353" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-5354" />;
      case 'suspended':
        return <StatusBadge status="info" text="Suspended" data-id="element-5355" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-5356" />;
    }
  };
  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };
  const tabs = [{
    id: 'merchant-kyc',
    label: 'Merchant KYC'
  }, {
    id: 'business-details',
    label: 'Business Details'
  }, {
    id: 'account-details',
    label: 'Account Details'
  }, {
    id: 'limit-management',
    label: 'Limit Management'
  }, {
    id: 'payment-methods',
    label: 'Payment Methods'
  }, {
    id: 'currency-setup',
    label: 'Currency Setup'
  }, {
    id: 'document-uploads',
    label: 'Document Uploads'
  }, {
    id: 'compliance-screening',
    label: 'Compliance Screening'
  }, {
    id: 'location-tagging',
    label: 'Location Tagging'
  }];
  return <div className="space-y-6" data-id="element-5357">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-5358">
        <div data-id="element-5359">
          <button onClick={() => navigate('/merchants')} className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" data-id="element-5360">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-5361" />
            Back to Merchants
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1" data-id="element-5362">
            {isEditing ? 'Edit Merchant' : 'New Merchant'}
          </h1>
        </div>
        <div className="mt-3 sm:mt-0 flex items-center space-x-3" data-id="element-5363">
          {formStatus === 'submitted' && <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-3 py-1 rounded-md text-sm flex items-center" data-id="element-5364">
              <CheckIcon className="h-4 w-4 mr-1" data-id="element-5365" />
              Submitted Successfully
            </div>}
          {formStatus === 'rejected' && <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 px-3 py-1 rounded-md text-sm flex items-center" data-id="element-5366">
              <XIcon className="h-4 w-4 mr-1" data-id="element-5367" />
              Rejected
            </div>}
          {isEditing && <div className="flex items-center space-x-2" data-id="element-5368">
              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5369">
                Status:
              </span>
              {getStatusBadge(merchant.status)}
            </div>}
          <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => navigate('/merchants')} data-id="element-5370">
            Cancel
          </button>
          {isEditing && <button type="button" className="inline-flex items-center px-4 py-2 border border-purple-300 dark:border-purple-600 rounded-md shadow-sm text-sm font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30" onClick={handleStatusChange} data-id="element-5371">
              <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-5372" />
              Change Status
            </button>}
          <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" onClick={handleSubmit} data-id="element-5373">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-5374" />
            {isEditing ? 'Update' : 'Submit'} Merchant
          </button>
        </div>
      </div>
      {/* Form Status */}
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-5375">
        <div className="flex items-center justify-between" data-id="element-5376">
          <div className="flex items-center" data-id="element-5377">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-2" data-id="element-5378">
              Form Status:
            </span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${formStatus === 'draft' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' : formStatus === 'submitted' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' : formStatus === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`} data-id="element-5379">
              {formStatus.charAt(0).toUpperCase() + formStatus.slice(1)}
            </span>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5380">
            {isEditing ? 'Last updated:' : 'Created:'}{' '}
            {new Date().toLocaleDateString()}
          </div>
        </div>
        {isEditing && merchant.statusHistory.length > 0 && <div className="mt-3 flex justify-between items-center" data-id="element-5381">
            <div className="flex items-center" data-id="element-5382">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-2" data-id="element-5383">
                Merchant Status:
              </span>
              {getStatusBadge(merchant.status)}
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400" data-id="element-5384">
                Last changed:{' '}
                {formatDateTime(merchant.statusHistory[0].changedAt)}
              </span>
            </div>
            <button type="button" className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" onClick={() => setShowStatusHistoryModal(true)} data-id="element-5385">
              <HistoryIcon className="h-4 w-4 mr-1" data-id="element-5386" />
              View Status History
            </button>
          </div>}
        {formStatus === 'rejected' && <div className="mt-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded p-3 flex items-start" data-id="element-5387">
            <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5" data-id="element-5388" />
            <div data-id="element-5389">
              <p className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-5390">
                Application Rejected
              </p>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1" data-id="element-5391">
                Missing required documentation. Please upload proof of business
                registration and resubmit.
              </p>
            </div>
          </div>}
      </div>
      {/* Form Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700" data-id="element-5392">
        <div className="overflow-x-auto" data-id="element-5393">
          <nav className="-mb-px flex space-x-8" data-id="element-5394">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'}`} data-id="element-5395">
                {tab.label}
              </button>)}
          </nav>
        </div>
      </div>
      {/* Form Content */}
      <form onSubmit={handleSubmit} data-id="element-5396">
        {/* Merchant KYC */}
        {activeTab === 'merchant-kyc' && <Card data-id="element-5397">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5398">
              <div data-id="element-5399">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5400">
                  CNIC Number <span className="text-red-500" data-id="element-5401">*</span>
                </label>
                <div className="relative" data-id="element-5402">
                  <input type="text" name="cnic" value={merchant.cnic} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="00000-0000000-0" required data-id="element-5403" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-5404">
                    <HelpCircleIcon className="h-4 w-4 text-gray-400 cursor-help" title="National ID Card Number" data-id="element-5405" />
                  </div>
                </div>
              </div>
              <div data-id="element-5406">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5407">
                  NTN
                </label>
                <input type="text" name="ntn" value={merchant.ntn} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="National Tax Number" data-id="element-5408" />
              </div>
              <div data-id="element-5409">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5410">
                  Business Type <span className="text-red-500" data-id="element-5411">*</span>
                </label>
                <select name="businessType" value={merchant.businessType} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-5412">
                  <option value="" data-id="element-5413">Select Business Type</option>
                  <option value="Sole Proprietorship" data-id="element-5414">
                    Sole Proprietorship
                  </option>
                  <option value="Partnership" data-id="element-5415">Partnership</option>
                  <option value="Corporation" data-id="element-5416">Corporation</option>
                  <option value="LLC" data-id="element-5417">Limited Liability Company (LLC)</option>
                  <option value="Non-Profit" data-id="element-5418">Non-Profit Organization</option>
                </select>
              </div>
              <div data-id="element-5419">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5420">
                  Business Registration Number{' '}
                  <span className="text-red-500" data-id="element-5421">*</span>
                </label>
                <input type="text" name="registrationNumber" value={merchant.registrationNumber} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Business Registration Number" required data-id="element-5422" />
              </div>
              <div data-id="element-5423">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5424">
                  Parent Merchant
                </label>
                <select name="parentMerchantId" value={merchant.parentMerchantId} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-5425">
                  <option value="" data-id="element-5426">Select Parent Merchant (Optional)</option>
                  {parentMerchantsData.map(parent => <option key={parent.id} value={parent.id} data-id="element-5427">
                      {parent.name}
                    </option>)}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-5428">
                  If selected, this merchant will be added as a child merchant
                </p>
              </div>
              <div data-id="element-5429">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5430">
                  Verification Status
                </label>
                <div className="flex items-center space-x-4 mt-2" data-id="element-5431">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${merchant.verificationStatus === 'verified' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : merchant.verificationStatus === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'}`} data-id="element-5432">
                    {merchant.verificationStatus.charAt(0).toUpperCase() + merchant.verificationStatus.slice(1)}
                  </div>
                  {merchant.verificationStatus === 'verified' && <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5433">
                      Verified on {new Date().toLocaleDateString()}
                    </span>}
                </div>
              </div>
              <div className="md:col-span-2" data-id="element-5434">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5435">
                  Beneficial Owners
                </label>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-5436">
                  <div className="flex justify-between items-center" data-id="element-5437">
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5438">
                      Add individuals who own 25% or more of the business
                    </p>
                    <button type="button" className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50" data-id="element-5439">
                      Add Owner
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Card>}
        {/* Business Details */}
        {activeTab === 'business-details' && <Card data-id="element-5440">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5441">
              <div data-id="element-5442">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5443">
                  Business Name <span className="text-red-500" data-id="element-5444">*</span>
                </label>
                <input type="text" name="businessName" value={merchant.businessName} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Legal Business Name" required data-id="element-5445" />
              </div>
              <div data-id="element-5446">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5447">
                  Industry Type <span className="text-red-500" data-id="element-5448">*</span>
                </label>
                <select name="industryType" value={merchant.industryType} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-5449">
                  <option value="" data-id="element-5450">Select Industry</option>
                  <option value="Retail" data-id="element-5451">Retail</option>
                  <option value="E-commerce" data-id="element-5452">E-commerce</option>
                  <option value="Food & Beverage" data-id="element-5453">Food & Beverage</option>
                  <option value="Healthcare" data-id="element-5454">Healthcare</option>
                  <option value="Technology" data-id="element-5455">Technology</option>
                  <option value="Travel" data-id="element-5456">Travel & Hospitality</option>
                  <option value="Education" data-id="element-5457">Education</option>
                  <option value="Financial" data-id="element-5458">Financial Services</option>
                  <option value="Other" data-id="element-5459">Other</option>
                </select>
              </div>
              <div data-id="element-5460">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5461">
                  MCC (Merchant Category Code){' '}
                  <span className="text-red-500" data-id="element-5462">*</span>
                </label>
                <div className="relative" data-id="element-5463">
                  <input type="text" name="mcc" value={merchant.mcc} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="e.g. 5411" required data-id="element-5464" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-5465">
                    <HelpCircleIcon className="h-4 w-4 text-gray-400 cursor-help" title="4-digit code that classifies the business type" data-id="element-5466" />
                  </div>
                </div>
              </div>
              <div className="md:col-span-2" data-id="element-5467">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5468">
                  Business Address <span className="text-red-500" data-id="element-5469">*</span>
                </label>
                <textarea name="address" value={merchant.address} onChange={handleInputChange} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Full business address" required data-id="element-5470" />
              </div>
              <div data-id="element-5471">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5472">
                  Contact Person <span className="text-red-500" data-id="element-5473">*</span>
                </label>
                <input type="text" name="contactPerson" value={merchant.contactPerson} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Full name of primary contact" required data-id="element-5474" />
              </div>
              <div data-id="element-5475">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5476">
                  Contact Email <span className="text-red-500" data-id="element-5477">*</span>
                </label>
                <input type="email" name="contactEmail" value={merchant.contactEmail} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="email@example.com" required data-id="element-5478" />
              </div>
              <div data-id="element-5479">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5480">
                  Contact Phone <span className="text-red-500" data-id="element-5481">*</span>
                </label>
                <input type="tel" name="contactPhone" value={merchant.contactPhone} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="+1 (123) 456-7890" required data-id="element-5482" />
              </div>
            </div>
          </Card>}
        {/* Account Details */}
        {activeTab === 'account-details' && <Card data-id="element-5483">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5484">
              <div className="md:col-span-2" data-id="element-5485">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5486">
                  Settlement Type <span className="text-red-500" data-id="element-5487">*</span>
                </label>
                <select name="settlementType" value={merchant.settlementType} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-5488">
                  <option value="individual" data-id="element-5489">Individual Pay</option>
                  <option value="central" data-id="element-5490">Central Pay</option>
                </select>
                {merchant.settlementType === 'central' && <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md" data-id="element-5491">
                    <span className="font-medium" data-id="element-5492">Central Pay Selected:</span>{' '}
                    Settlement will be done in the control account. No
                    additional account details are required.
                  </p>}
              </div>
              {merchant.settlementType === 'individual' && <>
                  <div data-id="element-5493">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5494">
                      Bank Account Number{' '}
                      <span className="text-red-500" data-id="element-5495">*</span>
                    </label>
                    <input type="text" name="bankAccount" value={merchant.bankAccount} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Bank Account Number" required data-id="element-5496" />
                  </div>
                  <div data-id="element-5497">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5498">
                      Bank Name <span className="text-red-500" data-id="element-5499">*</span>
                    </label>
                    <input type="text" name="bankName" value={merchant.bankName} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Bank Name" required data-id="element-5500" />
                  </div>
                  <div data-id="element-5501">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5502">
                      Branch Code
                    </label>
                    <input type="text" name="branchCode" value={merchant.branchCode} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Branch Code" data-id="element-5503" />
                  </div>
                  <div data-id="element-5504">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5505">
                      Settlement Currency{' '}
                      <span className="text-red-500" data-id="element-5506">*</span>
                    </label>
                    <select name="settlementCurrency" value={merchant.settlementCurrency} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-5507">
                      <option value="USD" data-id="element-5508">USD - US Dollar</option>
                      <option value="EUR" data-id="element-5509">EUR - Euro</option>
                      <option value="GBP" data-id="element-5510">GBP - British Pound</option>
                      <option value="JPY" data-id="element-5511">JPY - Japanese Yen</option>
                      <option value="CAD" data-id="element-5512">CAD - Canadian Dollar</option>
                      <option value="AUD" data-id="element-5513">AUD - Australian Dollar</option>
                      <option value="AED" data-id="element-5514">AED - UAE Dirham</option>
                      <option value="SAR" data-id="element-5515">SAR - Saudi Riyal</option>
                    </select>
                  </div>
                  <div data-id="element-5516">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5517">
                      Settlement Frequency{' '}
                      <span className="text-red-500" data-id="element-5518">*</span>
                    </label>
                    <select name="settlementFrequency" value={merchant.settlementFrequency} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-5519">
                      <option value="daily" data-id="element-5520">Daily</option>
                      <option value="weekly" data-id="element-5521">Weekly</option>
                      <option value="biweekly" data-id="element-5522">Bi-weekly</option>
                      <option value="monthly" data-id="element-5523">Monthly</option>
                    </select>
                  </div>
                </>}
            </div>
          </Card>}
        {/* Limit Management */}
        {activeTab === 'limit-management' && <Card data-id="element-5524">
            <div className="space-y-6" data-id="element-5525">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-5526">
                <div data-id="element-5527">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5528">
                    Payment Mode <span className="text-red-500" data-id="element-5529">*</span>
                  </label>
                  <select name="paymentMode" value={newLimit.paymentMode} onChange={handleNewLimitChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-5530">
                    <option value="" data-id="element-5531">Select Payment Mode</option>
                    {paymentModes.map(mode => <option key={mode} value={mode} data-id="element-5532">
                        {mode}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-5533">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5534">
                    Amount <span className="text-red-500" data-id="element-5535">*</span>
                  </label>
                  <div className="relative mt-1 rounded-md shadow-sm" data-id="element-5536">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-5537">
                      <span className="text-gray-500 dark:text-gray-400 sm:text-sm" data-id="element-5538">
                        $
                      </span>
                    </div>
                    <input type="number" name="amount" value={newLimit.amount || ''} onChange={handleNewLimitChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-7 pr-3 text-sm" placeholder="0.00" data-id="element-5539" />
                  </div>
                </div>
                <div data-id="element-5540">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5541">
                    Frequency <span className="text-red-500" data-id="element-5542">*</span>
                  </label>
                  <select name="frequency" value={newLimit.frequency} onChange={handleNewLimitChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-5543">
                    <option value="" data-id="element-5544">Select Frequency</option>
                    {frequencyOptions.map(freq => <option key={freq} value={freq} data-id="element-5545">
                        {freq}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-5546">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5547">
                    Effective Date <span className="text-red-500" data-id="element-5548">*</span>
                  </label>
                  <div className="relative" data-id="element-5549">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-5550">
                      <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-5551" />
                    </div>
                    <input type="date" name="effectiveDate" value={newLimit.effectiveDate} onChange={handleNewLimitChange} min={new Date().toISOString().split('T')[0]} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-10 pr-3 text-sm" data-id="element-5552" />
                  </div>
                </div>
                <div data-id="element-5553">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5554">
                    End Date
                  </label>
                  <div className="relative" data-id="element-5555">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-5556">
                      <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-5557" />
                    </div>
                    <input type="date" name="endDate" value={newLimit.endDate} onChange={handleNewLimitChange} min={newLimit.effectiveDate || new Date().toISOString().split('T')[0]} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-10 pr-3 text-sm" data-id="element-5558" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-5559">
                    Leave empty for no end date
                  </p>
                </div>
                <div className="flex items-end" data-id="element-5560">
                  <button type="button" onClick={addNewLimit} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-5561">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-5562" />
                    Add Limit
                  </button>
                </div>
              </div>
              {limitError && <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded p-3 text-sm text-red-700 dark:text-red-400" data-id="element-5563">
                  {limitError}
                </div>}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-5564">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-5565">
                  Configured Limits
                </h3>
                {merchant.limits.length > 0 ? <div className="overflow-x-auto" data-id="element-5566">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5567">
                      <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-5568">
                        <tr data-id="element-5569">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-5570">
                            Payment Mode
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-5571">
                            Amount
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-5572">
                            Frequency
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-5573">
                            Effective Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-5574">
                            End Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-5575">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5576">
                        {merchant.limits.map(limit => <tr key={limit.id} data-id="element-5577">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-5578">
                              {limit.paymentMode}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-5579">
                              ${parseFloat(limit.amount.toString()).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-5580">
                              {limit.frequency}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-5581">
                              {new Date(limit.effectiveDate).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-5582">
                              {limit.endDate ? new Date(limit.endDate).toLocaleDateString() : 'No end date'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5583">
                              <button type="button" onClick={() => removeLimit(limit.id)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300" data-id="element-5584">
                                <TrashIcon className="h-5 w-5" data-id="element-5585" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div> : <div className="text-center py-4 text-gray-500 dark:text-gray-400" data-id="element-5586">
                    No limits configured yet. Add a limit using the form above.
                  </div>}
              </div>
              <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3" data-id="element-5587">
                <p className="text-sm text-yellow-800 dark:text-yellow-300 flex items-center" data-id="element-5588">
                  <AlertCircleIcon className="h-4 w-4 mr-2" data-id="element-5589" />
                  Setting appropriate limits helps prevent fraud and manage
                  risk.
                </p>
              </div>
            </div>
          </Card>}
        {/* Payment Methods */}
        {activeTab === 'payment-methods' && <PaymentMethodsTab configs={paymentConfigs} onChange={configs => setPaymentConfigs(configs)} data-id="element-5590" />}
        {/* Currency Setup */}
        {activeTab === 'currency-setup' && <Card data-id="element-5591">
            <div className="space-y-4" data-id="element-5592">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5593">
                Select the currencies this merchant is allowed to accept:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5594">
                <div className="flex items-center" data-id="element-5595">
                  <input type="checkbox" id="currency-USD" name="currency-USD" checked={merchant.allowedCurrencies.includes('USD')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-5596" />
                  <label htmlFor="currency-USD" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-5597">
                    USD - US Dollar
                  </label>
                </div>
                <div className="flex items-center" data-id="element-5598">
                  <input type="checkbox" id="currency-EUR" name="currency-EUR" checked={merchant.allowedCurrencies.includes('EUR')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-5599" />
                  <label htmlFor="currency-EUR" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-5600">
                    EUR - Euro
                  </label>
                </div>
                <div className="flex items-center" data-id="element-5601">
                  <input type="checkbox" id="currency-GBP" name="currency-GBP" checked={merchant.allowedCurrencies.includes('GBP')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-5602" />
                  <label htmlFor="currency-GBP" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-5603">
                    GBP - British Pound
                  </label>
                </div>
                <div className="flex items-center" data-id="element-5604">
                  <input type="checkbox" id="currency-JPY" name="currency-JPY" checked={merchant.allowedCurrencies.includes('JPY')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-5605" />
                  <label htmlFor="currency-JPY" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-5606">
                    JPY - Japanese Yen
                  </label>
                </div>
                <div className="flex items-center" data-id="element-5607">
                  <input type="checkbox" id="currency-CAD" name="currency-CAD" checked={merchant.allowedCurrencies.includes('CAD')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-5608" />
                  <label htmlFor="currency-CAD" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-5609">
                    CAD - Canadian Dollar
                  </label>
                </div>
                <div className="flex items-center" data-id="element-5610">
                  <input type="checkbox" id="currency-AUD" name="currency-AUD" checked={merchant.allowedCurrencies.includes('AUD')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-5611" />
                  <label htmlFor="currency-AUD" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-5612">
                    AUD - Australian Dollar
                  </label>
                </div>
                <div className="flex items-center" data-id="element-5613">
                  <input type="checkbox" id="currency-AED" name="currency-AED" checked={merchant.allowedCurrencies.includes('AED')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-5614" />
                  <label htmlFor="currency-AED" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-5615">
                    AED - UAE Dirham
                  </label>
                </div>
                <div className="flex items-center" data-id="element-5616">
                  <input type="checkbox" id="currency-SAR" name="currency-SAR" checked={merchant.allowedCurrencies.includes('SAR')} onChange={handleCurrencyChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-5617" />
                  <label htmlFor="currency-SAR" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-5618">
                    SAR - Saudi Riyal
                  </label>
                </div>
              </div>
              <div className="mt-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded p-3" data-id="element-5619">
                <p className="text-sm text-blue-800 dark:text-blue-300 flex items-start" data-id="element-5620">
                  <HelpCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-5621" />
                  <span data-id="element-5622">
                    <strong data-id="element-5623">Note:</strong> Settlement will always be in the
                    merchant's settlement currency. Exchange rates will be
                    applied for transactions in other currencies.
                  </span>
                </p>
              </div>
              <div className="mt-6" data-id="element-5624">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5625">
                  Exchange Rate Markup (%)
                </label>
                <div className="flex items-center" data-id="element-5626">
                  <input type="number" className="w-24 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="2.5" defaultValue="2.5" step="0.1" min="0" data-id="element-5627" />
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-5628">
                    % added to the base exchange rate
                  </span>
                </div>
              </div>
            </div>
          </Card>}
        {/* Document Uploads */}
        {activeTab === 'document-uploads' && <Card data-id="element-5629">
            <div className="space-y-6" data-id="element-5630">
              <div data-id="element-5631">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-5632">
                  CNIC Document
                </h3>
                <div className={`border-2 border-dashed rounded-md px-6 pt-5 pb-6 ${merchant.documents.cnic.uploaded ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-5633">
                  <div className="space-y-2 text-center" data-id="element-5634">
                    {merchant.documents.cnic.uploaded ? <>
                        <CheckIcon className="mx-auto h-12 w-12 text-green-500" data-id="element-5635" />
                        <div className="text-sm text-green-600 dark:text-green-400" data-id="element-5636">
                          <p className="font-medium" data-id="element-5637">
                            Document uploaded successfully
                          </p>
                          <p data-id="element-5638">{merchant.documents.cnic.name}</p>
                        </div>
                        <button type="button" className="mt-2 inline-flex items-center px-3 py-1 border border-transparent rounded-md text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50" data-id="element-5639">
                          Remove
                        </button>
                      </> : <>
                        <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-5640" />
                        <div className="flex text-sm text-gray-600 dark:text-gray-400" data-id="element-5641">
                          <label htmlFor="cnic-upload" className="relative cursor-pointer rounded-md bg-white dark:bg-gray-800 font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 focus-within:outline-none" data-id="element-5642">
                            <span data-id="element-5643">Upload a file</span>
                            <input id="cnic-upload" name="cnic-upload" type="file" className="sr-only" onChange={() => handleFileUpload('cnic')} data-id="element-5644" />
                          </label>
                          <p className="pl-1" data-id="element-5645">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5646">
                          PNG, JPG, PDF up to 10MB
                        </p>
                      </>}
                  </div>
                </div>
              </div>
              <div data-id="element-5647">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-5648">
                  Proof of Address
                </h3>
                <div className={`border-2 border-dashed rounded-md px-6 pt-5 pb-6 ${merchant.documents.proofOfAddress.uploaded ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-5649">
                  <div className="space-y-2 text-center" data-id="element-5650">
                    {merchant.documents.proofOfAddress.uploaded ? <>
                        <CheckIcon className="mx-auto h-12 w-12 text-green-500" data-id="element-5651" />
                        <div className="text-sm text-green-600 dark:text-green-400" data-id="element-5652">
                          <p className="font-medium" data-id="element-5653">
                            Document uploaded successfully
                          </p>
                          <p data-id="element-5654">{merchant.documents.proofOfAddress.name}</p>
                        </div>
                        <button type="button" className="mt-2 inline-flex items-center px-3 py-1 border border-transparent rounded-md text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50" data-id="element-5655">
                          Remove
                        </button>
                      </> : <>
                        <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-5656" />
                        <div className="flex text-sm text-gray-600 dark:text-gray-400" data-id="element-5657">
                          <label htmlFor="address-upload" className="relative cursor-pointer rounded-md bg-white dark:bg-gray-800 font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 focus-within:outline-none" data-id="element-5658">
                            <span data-id="element-5659">Upload a file</span>
                            <input id="address-upload" name="address-upload" type="file" className="sr-only" onChange={() => handleFileUpload('proofOfAddress')} data-id="element-5660" />
                          </label>
                          <p className="pl-1" data-id="element-5661">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5662">
                          PNG, JPG, PDF up to 10MB
                        </p>
                      </>}
                  </div>
                </div>
              </div>
              <div data-id="element-5663">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-5664">
                  Business Registration
                </h3>
                <div className={`border-2 border-dashed rounded-md px-6 pt-5 pb-6 ${merchant.documents.businessRegistration.uploaded ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-5665">
                  <div className="space-y-2 text-center" data-id="element-5666">
                    {merchant.documents.businessRegistration.uploaded ? <>
                        <CheckIcon className="mx-auto h-12 w-12 text-green-500" data-id="element-5667" />
                        <div className="text-sm text-green-600 dark:text-green-400" data-id="element-5668">
                          <p className="font-medium" data-id="element-5669">
                            Document uploaded successfully
                          </p>
                          <p data-id="element-5670">{merchant.documents.businessRegistration.name}</p>
                        </div>
                        <button type="button" className="mt-2 inline-flex items-center px-3 py-1 border border-transparent rounded-md text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50" data-id="element-5671">
                          Remove
                        </button>
                      </> : <>
                        <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-5672" />
                        <div className="flex text-sm text-gray-600 dark:text-gray-400" data-id="element-5673">
                          <label htmlFor="registration-upload" className="relative cursor-pointer rounded-md bg-white dark:bg-gray-800 font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 focus-within:outline-none" data-id="element-5674">
                            <span data-id="element-5675">Upload a file</span>
                            <input id="registration-upload" name="registration-upload" type="file" className="sr-only" onChange={() => handleFileUpload('businessRegistration')} data-id="element-5676" />
                          </label>
                          <p className="pl-1" data-id="element-5677">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5678">
                          PNG, JPG, PDF up to 10MB
                        </p>
                      </>}
                  </div>
                </div>
              </div>
              <div data-id="element-5679">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-5680">
                  Bank Letter
                </h3>
                <div className={`border-2 border-dashed rounded-md px-6 pt-5 pb-6 ${merchant.documents.bankLetter.uploaded ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-5681">
                  <div className="space-y-2 text-center" data-id="element-5682">
                    {merchant.documents.bankLetter.uploaded ? <>
                        <CheckIcon className="mx-auto h-12 w-12 text-green-500" data-id="element-5683" />
                        <div className="text-sm text-green-600 dark:text-green-400" data-id="element-5684">
                          <p className="font-medium" data-id="element-5685">
                            Document uploaded successfully
                          </p>
                          <p data-id="element-5686">{merchant.documents.bankLetter.name}</p>
                        </div>
                        <button type="button" className="mt-2 inline-flex items-center px-3 py-1 border border-transparent rounded-md text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50" data-id="element-5687">
                          Remove
                        </button>
                      </> : <>
                        <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-5688" />
                        <div className="flex text-sm text-gray-600 dark:text-gray-400" data-id="element-5689">
                          <label htmlFor="bank-upload" className="relative cursor-pointer rounded-md bg-white dark:bg-gray-800 font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 focus-within:outline-none" data-id="element-5690">
                            <span data-id="element-5691">Upload a file</span>
                            <input id="bank-upload" name="bank-upload" type="file" className="sr-only" onChange={() => handleFileUpload('bankLetter')} data-id="element-5692" />
                          </label>
                          <p className="pl-1" data-id="element-5693">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5694">
                          PNG, JPG, PDF up to 10MB
                        </p>
                      </>}
                  </div>
                </div>
              </div>
            </div>
          </Card>}
        {/* Compliance Screening */}
        {activeTab === 'compliance-screening' && <Card data-id="element-5695">
            <div className="space-y-6" data-id="element-5696">
              <div className={`p-4 rounded-md ${merchant.complianceStatus === 'passed' ? 'bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800' : merchant.complianceStatus === 'failed' ? 'bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800' : 'bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800'}`} data-id="element-5697">
                <div className="flex" data-id="element-5698">
                  <div className="flex-shrink-0" data-id="element-5699">
                    {merchant.complianceStatus === 'passed' ? <CheckIcon className="h-5 w-5 text-green-400" data-id="element-5700" /> : merchant.complianceStatus === 'failed' ? <XIcon className="h-5 w-5 text-red-400" data-id="element-5701" /> : <AlertCircleIcon className="h-5 w-5 text-yellow-400" data-id="element-5702" />}
                  </div>
                  <div className="ml-3" data-id="element-5703">
                    <h3 className={`text-sm font-medium ${merchant.complianceStatus === 'passed' ? 'text-green-800 dark:text-green-300' : merchant.complianceStatus === 'failed' ? 'text-red-800 dark:text-red-300' : 'text-yellow-800 dark:text-yellow-300'}`} data-id="element-5704">
                      AML Screening:{' '}
                      {merchant.complianceStatus.charAt(0).toUpperCase() + merchant.complianceStatus.slice(1)}
                    </h3>
                    <div className={`mt-2 text-sm ${merchant.complianceStatus === 'passed' ? 'text-green-700 dark:text-green-400' : merchant.complianceStatus === 'failed' ? 'text-red-700 dark:text-red-400' : 'text-yellow-700 dark:text-yellow-400'}`} data-id="element-5705">
                      <p data-id="element-5706">
                        {merchant.complianceStatus === 'passed' ? 'All compliance checks have passed successfully.' : merchant.complianceStatus === 'failed' ? 'Compliance check failed. See details below.' : 'Compliance check is pending. Submit to initiate the check.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-5707">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5708">
                  Compliance Review Comments
                </label>
                <textarea name="complianceNotes" value={merchant.complianceNotes} onChange={handleInputChange} rows={4} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter any notes or comments related to compliance review" data-id="element-5709" />
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-5710">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-5711">
                  Blacklist Check
                </h3>
                <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-5712">
                  <div className="flex items-center" data-id="element-5713">
                    <span className="w-4 h-4 rounded-full bg-green-500 mr-2" data-id="element-5714"></span>
                    <span className="text-sm" data-id="element-5715">OFAC Sanctions List</span>
                  </div>
                  <span className="text-sm text-green-600 dark:text-green-400" data-id="element-5716">
                    Passed
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-5717">
                  <div className="flex items-center" data-id="element-5718">
                    <span className="w-4 h-4 rounded-full bg-green-500 mr-2" data-id="element-5719"></span>
                    <span className="text-sm" data-id="element-5720">PEP Check</span>
                  </div>
                  <span className="text-sm text-green-600 dark:text-green-400" data-id="element-5721">
                    Passed
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700" data-id="element-5722">
                  <div className="flex items-center" data-id="element-5723">
                    <span className="w-4 h-4 rounded-full bg-green-500 mr-2" data-id="element-5724"></span>
                    <span className="text-sm" data-id="element-5725">Adverse Media</span>
                  </div>
                  <span className="text-sm text-green-600 dark:text-green-400" data-id="element-5726">
                    Passed
                  </span>
                </div>
                <div className="flex items-center justify-between py-2" data-id="element-5727">
                  <div className="flex items-center" data-id="element-5728">
                    <span className="w-4 h-4 rounded-full bg-green-500 mr-2" data-id="element-5729"></span>
                    <span className="text-sm" data-id="element-5730">Local Blacklist</span>
                  </div>
                  <span className="text-sm text-green-600 dark:text-green-400" data-id="element-5731">
                    Passed
                  </span>
                </div>
              </div>
              <div className="flex justify-end" data-id="element-5732">
                <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-5733">
                  Re-run Compliance Check
                </button>
              </div>
            </div>
          </Card>}
        {/* Location Tagging */}
        {activeTab === 'location-tagging' && <Card data-id="element-5734">
            <div className="space-y-4" data-id="element-5735">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5736">
                Mark the geo-coordinates of the merchant's physical location on
                the map:
              </p>
              <div className="h-96 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center" data-id="element-5737">
                <div className="text-center" data-id="element-5738">
                  <MapPinIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600" data-id="element-5739" />
                  <p className="mt-2 text-gray-500 dark:text-gray-400" data-id="element-5740">
                    Interactive map would be displayed here
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5741">
                <div data-id="element-5742">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5743">
                    Latitude
                  </label>
                  <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="e.g. 40.7128" value={merchant.location.lat || ''} readOnly data-id="element-5744" />
                </div>
                <div data-id="element-5745">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5746">
                    Longitude
                  </label>
                  <input type="text" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="e.g. -74.0060" value={merchant.location.lng || ''} readOnly data-id="element-5747" />
                </div>
              </div>
              <div className="flex space-x-3" data-id="element-5748">
                <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-5749">
                  Use Current Location
                </button>
                <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-5750">
                  Enter Coordinates Manually
                </button>
              </div>
              <div className="mt-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded p-3" data-id="element-5751">
                <p className="text-sm text-blue-800 dark:text-blue-300 flex items-center" data-id="element-5752">
                  <HelpCircleIcon className="h-4 w-4 mr-2" data-id="element-5753" />
                  Location tagging helps with fraud prevention and enables
                  location-based services.
                </p>
              </div>
            </div>
          </Card>}
      </form>
      {/* Form Actions */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-5754">
        <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => navigate('/merchants')} data-id="element-5755">
          Cancel
        </button>
        <div className="flex space-x-3" data-id="element-5756">
          <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-5757">
            Save as Draft
          </button>
          <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" onClick={handleSubmit} data-id="element-5758">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-5759" />
            {isEditing ? 'Update' : 'Submit'} Merchant
          </button>
        </div>
      </div>
      {/* Change Status Modal */}
      <Modal isOpen={showStatusModal} onClose={() => setShowStatusModal(false)} title="Change Merchant Status" footer={<>
            <button onClick={() => setShowStatusModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-5761">
              Cancel
            </button>
            <button onClick={confirmStatusChange} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" disabled={!newStatus || newStatus === merchant.status} data-id="element-5762">
              Update Status
            </button>
          </>} data-id="element-5760">
        <div className="space-y-4" data-id="element-5763">
          <div className="flex items-center mb-4" data-id="element-5764">
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-5765">
              <BuildingIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-5766" />
            </div>
            <div data-id="element-5767">
              <h3 className="text-lg font-medium" data-id="element-5768">{merchant.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5769">
                Current Status: {getStatusBadge(merchant.status)}
              </p>
            </div>
          </div>
          <div data-id="element-5770">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5771">
              New Status
            </label>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-5772">
              <option value="" data-id="element-5773">Select Status</option>
              <option value="active" data-id="element-5774">Active</option>
              <option value="inactive" data-id="element-5775">Inactive</option>
              <option value="pending" data-id="element-5776">Pending</option>
              <option value="suspended" data-id="element-5777">Suspended</option>
            </select>
          </div>
          <div data-id="element-5778">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5779">
              Reason for Status Change
            </label>
            <textarea value={statusChangeReason} onChange={e => setStatusChangeReason(e.target.value)} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter reason for status change" required data-id="element-5780" />
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3" data-id="element-5781">
            <p className="text-sm text-yellow-800 dark:text-yellow-300 flex items-start" data-id="element-5782">
              <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-5783" />
              <span data-id="element-5784">
                <strong data-id="element-5785">Note:</strong> Changing a merchant's status may affect
                their ability to process transactions. This action will be
                recorded in the audit log.
              </span>
            </p>
          </div>
        </div>
      </Modal>
      {/* Status History Modal */}
      <Modal isOpen={showStatusHistoryModal} onClose={() => setShowStatusHistoryModal(false)} title="Status Change History" size="lg" data-id="element-5786">
        <div className="space-y-4" data-id="element-5787">
          <div className="flex items-center mb-4" data-id="element-5788">
            <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-5789">
              <BuildingIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-5790" />
            </div>
            <div data-id="element-5791">
              <h3 className="text-lg font-medium" data-id="element-5792">{merchant.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5793">
                Current Status: {getStatusBadge(merchant.status)}
              </p>
            </div>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-4" data-id="element-5794">
            <h4 className="font-medium text-gray-700 dark:text-gray-300" data-id="element-5795">
              Complete Status History
            </h4>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto" data-id="element-5796">
            {merchant.statusHistory.map((history, index) => <div key={index} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md" data-id="element-5797">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center" data-id="element-5798">
                  <div className="flex items-center mb-2 sm:mb-0" data-id="element-5799">
                    {history.previousStatus ? <div className="flex items-center" data-id="element-5800">
                        <span className="inline-block" data-id="element-5801">
                          {getStatusBadge(history.previousStatus)}
                        </span>
                        <ArrowRightIcon className="h-4 w-4 mx-2 text-gray-500 dark:text-gray-400" data-id="element-5802" />
                        <span className="inline-block" data-id="element-5803">
                          {getStatusBadge(history.newStatus)}
                        </span>
                      </div> : <div className="flex items-center" data-id="element-5804">
                        <span className="text-sm text-gray-500 dark:text-gray-400 mr-2" data-id="element-5805">
                          Initial status:
                        </span>
                        <span className="inline-block" data-id="element-5806">
                          {getStatusBadge(history.newStatus)}
                        </span>
                      </div>}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5807">
                    {formatDateTime(history.changedAt)}
                  </div>
                </div>
                <div className="mt-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-5808">
                  <span className="font-medium" data-id="element-5809">Reason:</span> {history.reason}
                </div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-5810">
                  Changed by: {history.changedBy}
                </div>
              </div>)}
            {merchant.statusHistory.length === 0 && <div className="text-center py-4 text-gray-500 dark:text-gray-400" data-id="element-5811">
                No status change history available.
              </div>}
          </div>
        </div>
      </Modal>
    </div>;
};
export default MerchantForm;